// ============================================================================
// CHANGO V5.0 — Product Cost Breakdown (Task #119 / Round 12)
// ============================================================================
// View product cost analysis: material + process costs.
// Mount: /app/products/:code/cost-breakdown
//
// Sections:
//   1. Product header + total cost
//   2. Material cost tree (recursive BOM expansion)
//   3. Process cost table
//   4. Pie chart (SVG): material % vs process %
//
// boss/finance only (sales denied)
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

function PieChart({ material, process, total }) {
  if (total === 0) return <div className="text-slate-400 text-sm">No cost data</div>;

  const matPct = (material / total) * 100;
  const procPct = (process / total) * 100;

  // Draw SVG pie chart
  const radius = 60;
  const cx = 80, cy = 80;

  // Material arc (starting from 0°)
  const matStartAngle = 0;
  const matEndAngle = (matPct / 100) * 360;
  const matStartRad = (matStartAngle * Math.PI) / 180;
  const matEndRad = (matEndAngle * Math.PI) / 180;
  const matX1 = cx + radius * Math.cos(matStartRad);
  const matY1 = cy + radius * Math.sin(matStartRad);
  const matX2 = cx + radius * Math.cos(matEndRad);
  const matY2 = cy + radius * Math.sin(matEndRad);
  const matLarge = matPct > 50 ? 1 : 0;
  const matPath = `M ${cx} ${cy} L ${matX1} ${matY1} A ${radius} ${radius} 0 ${matLarge} 1 ${matX2} ${matY2} Z`;

  // Process arc
  const procStartAngle = matEndAngle;
  const procEndAngle = 360;
  const procStartRad = (procStartAngle * Math.PI) / 180;
  const procEndRad = (procEndAngle * Math.PI) / 180;
  const procX1 = cx + radius * Math.cos(procStartRad);
  const procY1 = cy + radius * Math.sin(procStartRad);
  const procX2 = cx + radius * Math.cos(procEndRad);
  const procY2 = cy + radius * Math.sin(procEndRad);
  const procLarge = procPct > 50 ? 1 : 0;
  const procPath = `M ${cx} ${cy} L ${procX1} ${procY1} A ${radius} ${radius} 0 ${procLarge} 1 ${procX2} ${procY2} Z`;

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 160 160" className="w-40 h-40">
        <path d={matPath} fill="#3b82f6" opacity="0.8" />
        <path d={procPath} fill="#ec4899" opacity="0.8" />
      </svg>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3b82f6' }} />
          <span>Material: {matPct.toFixed(1)}%</span>
          <span className="text-slate-500">¥{material.toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ec4899' }} />
          <span>Process: {procPct.toFixed(1)}%</span>
          <span className="text-slate-500">¥{process.toFixed(2)}</span>
        </div>
        <div className="pt-2 border-t">
          <span className="font-semibold">Total: ¥{total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function MaterialRow({ item, qty, depth = 0 }) {
  const cost = (item.cost_rmb || 0) * qty;
  const indent = depth * 20;
  return (
    <tr>
      <td className="px-4 py-2" style={{ paddingLeft: `${12 + indent}px` }}>
        <span className="font-mono text-xs">{item.code}</span>
      </td>
      <td className="px-4 py-2">{item.name}</td>
      <td className="px-4 py-2 text-right text-sm">{qty.toFixed(4)}</td>
      <td className="px-4 py-2 text-right text-sm">¥{(item.cost_rmb || 0).toFixed(2)}</td>
      <td className="px-4 py-2 text-right text-sm font-semibold">¥{cost.toFixed(2)}</td>
    </tr>
  );
}

export default function ProductCostBreakdown() {
  const { code } = useParams();
  const { user } = useAuth() || {};
  const isSales = user?.role === 'sales';
  const decodedCode = decodeURIComponent(code || '');

  const [product, setProduct] = useState(null);
  const [bomTree, setBomTree] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const [prodRes, treeRes, costRes, procRes] = await Promise.all([
          api.get(`/api/products/${decodedCode}`),
          api.get(`/api/products/${encodeURIComponent(decodedCode)}/bom-tree`),
          api.get(`/api/products/${encodeURIComponent(decodedCode)}/cost-breakdown`),
          api.get('/api/product-processes?product_code=' + encodeURIComponent(decodedCode))
        ]);

        setProduct(prodRes);
        setBomTree(treeRes);
        setCostBreakdown(costRes);
        setProcesses(procRes?.items || []);
      } catch (err) {
        setError(err.message || 'Failed to load cost breakdown');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [decodedCode]);

  if (isSales) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          权限不足：仅 boss/finance 可访问成本核算页面
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="px-6 py-12 text-center text-slate-400">Loading...</div>;
  }

  if (!product) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Product not found
        </div>
        <div className="mt-4">
          <Link to="/app/products" className="text-sm text-slate-600 hover:underline">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
        <div className="mt-4">
          <Link to="/app/products" className="text-sm text-slate-600 hover:underline">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const totalMaterial = costBreakdown?.total_material_cost ?? 0;
  const totalProcess = costBreakdown?.total_process_cost ?? 0;
  const totalCost = totalMaterial + totalProcess;

  // Flatten BOM tree for material table
  const flattenTree = (node, qty = 1, depth = 0) => {
    const rows = [];
    if (node.code) {
      rows.push({ ...node, qty, depth });
    }
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child) => {
        const childQty = qty * (child.qty_per_parent || 1);
        rows.push(...flattenTree(child, childQty, depth + 1));
      });
    }
    return rows;
  };

  const materialRows = bomTree ? flattenTree(bomTree) : [];

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">成本核算</h1>
          <p className="text-sm text-slate-500">
            {product.name} ({product.sku_code})
          </p>
        </div>
      </header>

      {/* ---- Cost Summary + Pie Chart ---- */}
      <div className="card mb-6 p-6">
        <h2 className="text-lg font-semibold mb-4">成本汇总</h2>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="text-sm text-slate-500 mb-2">总成本</div>
            <div className="text-3xl font-bold">¥{totalCost.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-2">
              <div>物料: ¥{totalMaterial.toFixed(2)}</div>
              <div>工序: ¥{totalProcess.toFixed(2)}</div>
            </div>
          </div>
          <PieChart material={totalMaterial} process={totalProcess} total={totalCost} />
        </div>
      </div>

      {/* ---- Material Cost Tree ---- */}
      <div className="card mb-6 p-6">
        <h2 className="text-lg font-semibold mb-4">物料成本清单</h2>
        {materialRows.length === 0 ? (
          <p className="text-slate-500 text-sm">No BOM defined</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Name</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Qty</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Unit Price</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {materialRows.map((item, idx) => (
                  <MaterialRow key={idx} item={item} qty={item.qty} depth={item.depth} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---- Process Cost Table ---- */}
      <div className="card mb-6 p-6">
        <h2 className="text-lg font-semibold mb-4">工序成本清单</h2>
        {processes.length === 0 ? (
          <p className="text-slate-500 text-sm">No processes assigned</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Name</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Std Time (min)</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Labor Rate</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Equipment</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {processes.map((proc) => {
                  const timeHours = (proc.standard_time_min || 0) / 60;
                  const laborCost = timeHours * (proc.hourly_rate_rmb || 0);
                  const equipCost = timeHours * (proc.equipment_cost_per_hour || 0);
                  const procCost = laborCost + equipCost;
                  return (
                    <tr key={proc.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-mono text-xs">{proc.process_code}</td>
                      <td className="px-4 py-2">{proc.process_name}</td>
                      <td className="px-4 py-2 text-right">{proc.standard_time_min || 0}</td>
                      <td className="px-4 py-2 text-right">¥{(proc.hourly_rate_rmb || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">¥{(proc.equipment_cost_per_hour || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-semibold">¥{procCost.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4">
        <Link to="/app/products" className="text-sm text-slate-600 hover:underline">
          Back to Products
        </Link>
      </div>
    </div>
  );
}
